import { useCallback, useMemo } from "react";
import { components, paths } from "@core/api/api";
import useAxios from "@hooks/useFetch";
import { CommonDataKeywordItem } from "./useCommonData";
import { useCommonDataContext, useMetrics } from "@core/CommonDataContext";
import {
    compareMetrics,
    CORRELATION_METRICS_ORDER,
    Metric,
} from "@models/Metric";
import { useExport } from "@api/useExport";

const CORRELATION_ENDPOINT: keyof paths = '/api/Correlation/Get';
const CORRELATION_EXPORT_ENDPOINT: keyof paths = '/api/Export/GetCorrelationPagePpt';

export type CorrelationExportRequest = components["schemas"]["FlavorTracker.Entities.DTO.Export.CorrelationPagePptExportRequest"];

export type CorrelationRequest = components["schemas"]["FlavorTracker.Entities.DTO.CorrelationPage.CorrelationPageRequest"];
export type CorrelationResponseItem = components["schemas"]["FlavorTracker.Entities.DTO.CorrelationPage.CorrelationPageTableRow"];
export type CorrelationItem = Omit<CorrelationResponseItem, 'metrics'> & {
    name: CommonDataKeywordItem["name"];
    macStage: CommonDataKeywordItem["macStage"] | null;
    heatScore: CommonDataKeywordItem["heatScore"] | null;
    subCategoriesIds: CommonDataKeywordItem["subCategoriesIds"];
    metrics: {
        id: Metric;
        name: string;
        value: string;
        numValue: number | null;
    }[];
};
export type CorrelationResponseRaw = components["schemas"]["FlavorTracker.Entities.DTO.CorrelationPage.CorrelationPageResponse"];

export type CorrelationResponse = Omit<CorrelationResponseRaw, 'tableData'> & {
    tableData: CorrelationItem[];
};


export function useCorrelation(payload: CorrelationRequest): {
    isLoading: boolean;
    data: CorrelationResponse;
} {
    const response = useAxios<CorrelationResponseRaw, CorrelationRequest>({
        url: CORRELATION_ENDPOINT,
        method: 'post',
        payload,
    });

    const { keywords = [] } = useCommonDataContext();
    const rawMetrics = useMetrics();
    const metrics = useMemo(
        () => (rawMetrics ?? [])
            .filter((i) => CORRELATION_METRICS_ORDER.includes(i.id as Metric))
            .sort((a, b) => compareMetrics(CORRELATION_METRICS_ORDER, a.id, b.id)),
        [rawMetrics],
    );

    const data = useMemo(
        (): CorrelationResponse => {
            return {
                raw: response.data?.raw ?? 0,
                tableData: (response.data?.tableData ?? []).map((item) => {
                    const id = item.id!;
                    const keyword = keywords[id] ?? null;

                    return {
                        ...item,
                        name: (keyword?.name ?? '').toUpperCase(),
                        macStage: keyword?.macStage === 0 ? null : keyword?.macStage,
                        heatScore: keyword?.heatScore ?? null,
                        subCategoriesIds: keyword?.subCategoriesIds ?? [],
                        metrics: metrics.map(m => {
                            const metricValue = item.metrics?.find(
                                (o) => o.metric === m.id
                            );

                            return {
                                id: m.id ?? Metric.NoData,
                                name: m.name ?? '',
                                value: metricValue?.raw ? `${metricValue.raw}%` : '-',
                                numValue: metricValue?.originalRaw ?? null,
                            };
                        }),
                    };
                })
            };
        },
        [response.data, keywords, metrics]
    );

    return {
        ...response,
        data,
    };
}

export function useCorrelationExport(payload: CorrelationExportRequest) {
    const { isLoading, get: originalGet } = useExport(CORRELATION_EXPORT_ENDPOINT, payload);

    const get = useCallback(
        () => {
            originalGet().catch(console.error)
        },
        [originalGet]
    );

    return {
        isLoading,
        get,
    }
}